/* ═══════════════════════════════════════════════════════
   src/content/domains/cards/grace.tsx
   All Grace domain cards with text pre-rendered as JSX.
   Edit `rendered` to update display. Edit `text` to update search.
   ═══════════════════════════════════════════════════════ */

import { DomainCard } from './types';
import {
  HOPE,
  STRESS,
  SPELLCAST_ROLL,
  VERY_CLOSE,
  CLOSE,
  FAR,
  VERY_FAR,
  MELEE,
  LONG_REST,
  SHORT_REST,
  VAULT,
  LOADOUT,
  ARMOR_SLOT,
  HIT_POINT,
  HIT_POINTS,
  ADVANTAGE,
  DISADVANTAGE,
} from '../../templates';

const graceCards: DomainCard[] = [
  {
    name: "Deft Deceiver",
    level: 1,
    domain: "Grace",
    type: "Ability",
    recallCost: 0,
    text: "**Spend a Hope** to gain advantage on a roll to deceive or trick someone into believing a lie you tell them.",
    rendered: (
      <>
        <p key={0} className="card-text-para"><strong className="inline-strong">Spend a {HOPE}</strong> to gain {ADVANTAGE} on a roll to deceive or trick someone into believing a lie you tell them.</p>
      </>
    ),
  },
  {
    name: "Enrapture",
    level: 1,
    domain: "Grace",
    type: "Spell",
    recallCost: 0,
    text: "Make a **Spellcast Roll** against a target within Close range. On a success, they become temporarily _Enraptured_. While _Enraptured_, a target's attention is fixed on you, narrowing their field of view and drowning out any sound but your voice. Once per rest on a success, you can **mark a Stress** to force the _Enraptured_ target to mark a Stress as well.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {CLOSE} range. On a success, they become temporarily <em>Enraptured</em>. While <em>Enraptured</em>, a target's attention is fixed on you, narrowing their field of view and drowning out any sound but your voice. Once per rest on a success, you can <strong className="inline-strong">mark a {STRESS}</strong> to force the <em>Enraptured</em> target to mark a {STRESS} as well.</p>
      </>
    ),
  },
  {
    name: "Inspirational Words",
    level: 1,
    domain: "Grace",
    type: "Ability",
    recallCost: 1,
    text: "Your speech is imbued with power. After a long rest, place a number of tokens on this card equal to your Presence. When you speak with an ally, you can spend a token from this card to give them one benefit from the following options:\n\n- Your ally clears a Stress.\n- Your ally clears a Hit Point.\n- Your ally gains a Hope.\n\nWhen you take a long rest, clear all unspent tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Your speech is imbued with power. After a {LONG_REST}, place a number of tokens on this card equal to your Presence. When you speak with an ally, you can spend a token from this card to give them one benefit from the following options:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Your ally clears a {STRESS}.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Your ally clears a {HIT_POINT}.</span></div>
        <div key={4} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>Your ally gains a {HOPE}.</span></div>
        <div key={5} className="card-text-gap" />
        <p key={6} className="card-text-para">When you take a {LONG_REST}, clear all unspent tokens.</p>
      </>
    ),
  },
  {
    name: "Tell No Lies",
    level: 2,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against a target within Very Close range. On a success, they can't lie to you while they remain within Close range, but they are not compelled to speak. If you ask them a question and they refuse to answer, they must mark a Stress and the effect ends. The target is typically unaware this spell has been cast on them until it causes them to utter the truth.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against a target within {VERY_CLOSE} range. On a success, they can't lie to you while they remain within {CLOSE} range, but they are not compelled to speak. If you ask them a question and they refuse to answer, they must mark a {STRESS} and the effect ends. The target is typically unaware this spell has been cast on them until it causes them to utter the truth.</p>
      </>
    ),
  },
  {
    name: "Troublemaker",
    level: 2,
    domain: "Grace",
    type: "Ability",
    recallCost: 2,
    text: "When you taunt or provoke a target within Far range, make a **Presence Roll** against them. Once per rest on a success, roll a number of **d4s** equal to your Proficiency. The target must mark Stress equal to the highest result rolled.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you taunt or provoke a target within {FAR} range, make a <strong className="inline-strong">Presence Roll</strong> against them. Once per rest on a success, roll a number of <strong className="inline-strong">d4s</strong> equal to your Proficiency. The target must mark {STRESS} equal to the highest result rolled.</p>
      </>
    ),
  },
  {
    name: "Hypnotic Shimmer",
    level: 3,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll** against all adversaries in front of you within Close range. Once per rest on a success, create an illusion of flashing colors and lights that temporarily _Stuns_ targets you succeed against and forces them to mark a Stress. While _Stunned_, they can't use reactions and can't take any other actions until they clear this condition.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against all adversaries in front of you within {CLOSE} range. Once per rest on a success, create an illusion of flashing colors and lights that temporarily <em>Stuns</em> targets you succeed against and forces them to mark a {STRESS}. While <em>Stunned</em>, they can't use reactions and can't take any other actions until they clear this condition.</p>
      </>
    ),
  },
  {
    name: "Invisibility",
    level: 3,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "Make a **Spellcast Roll (10)**. On a success, **mark a Stress** and choose yourself or an ally within Melee range to become _Invisible_. An _Invisible_ creature can't be seen except through magical means and attack rolls against them are made with disadvantage. Place a number of tokens on this card equal to your Spellcast trait. When the _Invisible_ creature takes an action, spend a token from this card. After the action that spends the last token is resolved, the effect ends.\n\nYou can only hold Invisibility on one creature at a time.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} (10). On a success, <strong className="inline-strong">mark a {STRESS}</strong> and choose yourself or an ally within {MELEE} range to become <em>Invisible</em>. An <em>Invisible</em> creature can't be seen except through magical means and attack rolls against them are made with {DISADVANTAGE}. Place a number of tokens on this card equal to your Spellcast trait. When the <em>Invisible</em> creature takes an action, spend a token from this card. After the action that spends the last token is resolved, the effect ends.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can only hold Invisibility on one creature at a time.</p>
      </>
    ),
  },
  {
    name: "Soothing Speech",
    level: 4,
    domain: "Grace",
    type: "Ability",
    recallCost: 1,
    text: "During a short rest, when you take the time to comfort another character while using the Tend to Wounds downtime move on them, clear an additional Hit Point on that character. When you do, you also clear 2 Hit Points.",
    rendered: (
      <>
        <p key={0} className="card-text-para">During a {SHORT_REST}, when you take the time to comfort another character while using the Tend to Wounds downtime move on them, clear an additional {HIT_POINT} on that character. When you do, you also clear 2 {HIT_POINTS}.</p>
      </>
    ),
  },
  {
    name: "Through Your Eyes",
    level: 4,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "Choose a target within Very Far range. You can see through their eyes and hear through their ears. You can transition between using your own senses or the target's freely until you cast another spell or until your next rest.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Choose a target within {VERY_FAR} range. You can see through their eyes and hear through their ears. You can transition between using your own senses or the target's freely until you cast another spell or until your next rest.</p>
      </>
    ),
  },
  {
    name: "Thought Delver",
    level: 5,
    domain: "Grace",
    type: "Spell",
    recallCost: 2,
    text: "You can peek into the minds of others. **Spend a Hope** to read the vague surface thoughts of a target within Far range. Make a **Spellcast Roll** against the target to delve for deeper, more hidden thoughts.\n\nOn a roll with Fear, the target might, at the GM's discretion, become aware that you're reading their thoughts.",
    rendered: (
      <>
        <p key={0} className="card-text-para">You can peek into the minds of others. <strong className="inline-strong">Spend a {HOPE}</strong> to read the vague surface thoughts of a target within {FAR} range. Make a {SPELLCAST_ROLL} against the target to delve for deeper, more hidden thoughts.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">On a roll with Fear, the target might, at the GM's discretion, become aware that you're reading their thoughts.</p>
      </>
    ),
  },
  {
    name: "Words of Discord",
    level: 5,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "Whisper words of discord to an adversary within Melee range and make a **Spellcast Roll (13)**. On a success, the target must mark a Stress and make an attack against another adversary instead of against you or your allies.\n\nOnce this attack is over, the target realizes what happened. The next time you cast Words of Discord on them, gain a -5 penalty to the Spellcast Roll.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Whisper words of discord to an adversary within {MELEE} range and make a {SPELLCAST_ROLL} (13). On a success, the target must mark a {STRESS} and make an attack against another adversary instead of against you or your allies.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">Once this attack is over, the target realizes what happened. The next time you cast Words of Discord on them, gain a -5 penalty to the {SPELLCAST_ROLL}.</p>
      </>
    ),
  },
  {
    name: "Never Upstaged",
    level: 6,
    domain: "Grace",
    type: "Ability",
    recallCost: 2,
    text: "When you mark 1 or more Hit Points from an attack, you can **mark a Stress** to place a number of tokens equal to the number of Hit Points you marked on this card. On your next successful attack, gain a +5 bonus to your damage roll for each token on this card, then clear all tokens.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When you mark 1 or more {HIT_POINTS} from an attack, you can <strong className="inline-strong">mark a {STRESS}</strong> to place a number of tokens equal to the number of {HIT_POINTS} you marked on this card. On your next successful attack, gain a +5 bonus to your damage roll for each token on this card, then clear all tokens.</p>
      </>
    ),
  },
  {
    name: "Share the Burden",
    level: 6,
    domain: "Grace",
    type: "Spell",
    recallCost: 0,
    text: "Once per rest, take on the Stress from a willing creature within Melee range. The target describes what intimate knowledge or emotions telepathically leak from their mind in this moment between you. Transfer any number of their marked Stress to you, then gain a Hope for each Stress transferred.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per rest, take on the {STRESS} from a willing creature within {MELEE} range. The target describes what intimate knowledge or emotions telepathically leak from their mind in this moment between you. Transfer any number of their marked {STRESS} to you, then gain a {HOPE} for each {STRESS} transferred.</p>
      </>
    ),
  },
  {
    name: "Endless Charisma",
    level: 7,
    domain: "Grace",
    type: "Ability",
    recallCost: 1,
    text: "After you make an action roll to persuade, lie, or garner favor, you can **spend a Hope** to reroll the Hope or Fear Die.",
    rendered: (
      <>
        <p key={0} className="card-text-para">After you make an action roll to persuade, lie, or garner favor, you can <strong className="inline-strong">spend a {HOPE}</strong> to reroll the {HOPE} or Fear Die.</p>
      </>
    ),
  },
  {
    name: "Grace-Touched",
    level: 7,
    domain: "Grace",
    type: "Ability",
    recallCost: 2,
    text: "When 4 or more of the domain cards in your loadout are from the Grace domain, gain the following benefits:\n\n- You can **mark an Armor Slot** instead of marking a Stress.\n- When you would force a target to mark a number of Hit Points, you can choose instead to force them to mark that number of Stress.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When 4 or more of the domain cards in your {LOADOUT} are from the Grace domain, gain the following benefits:</p>
        <div key={1} className="card-text-gap" />
        <div key={2} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>You can <strong className="inline-strong">mark an {ARMOR_SLOT}</strong> instead of marking a {STRESS}.</span></div>
        <div key={3} className="card-text-bullet"><span className="card-text-bullet__arrow">•</span><span>When you would force a target to mark a number of {HIT_POINTS}, you can choose instead to force them to mark that number of {STRESS}.</span></div>
      </>
    ),
  },
  {
    name: "Astral Projection",
    level: 8,
    domain: "Grace",
    type: "Spell",
    recallCost: 0,
    text: "Once per long rest, **mark a Stress** to create a projected copy of yourself that can appear anywhere you've been before.\n\nYou can see and hear through the projection as though it were you and affect the world as though you were there. A creature investigating the projection can tell it's of magical origin. This effect lasts until your next rest or your projection takes any damage.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, <strong className="inline-strong">mark a {STRESS}</strong> to create a projected copy of yourself that can appear anywhere you've been before.</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">You can see and hear through the projection as though it were you and affect the world as though you were there. A creature investigating the projection can tell it's of magical origin. This effect lasts until your next rest or your projection takes any damage.</p>
      </>
    ),
  },
  {
    name: "Mass Enrapture",
    level: 8,
    domain: "Grace",
    type: "Spell",
    recallCost: 3,
    text: "Make a **Spellcast Roll** against all targets within Far range. Targets you succeed against become temporarily _Enraptured_. While _Enraptured_, a target's attention is fixed on you, narrowing their field of view and drowning out any sound but your voice. **Mark a Stress** to force all _Enraptured_ targets to mark a Stress, ending this spell.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Make a {SPELLCAST_ROLL} against all targets within {FAR} range. Targets you succeed against become temporarily <em>Enraptured</em>. While <em>Enraptured</em>, a target's attention is fixed on you, narrowing their field of view and drowning out any sound but your voice. <strong className="inline-strong">Mark a {STRESS}</strong> to force all <em>Enraptured</em> targets to mark a {STRESS}, ending this spell.</p>
      </>
    ),
  },
  {
    name: "Copycat",
    level: 9,
    domain: "Grace",
    type: "Spell",
    recallCost: 3,
    text: "Once per long rest, this card can mimic the features of another domain card of level 8 or lower in another player's loadout. **Spend Hope equal to half the card's level** to gain access to the feature. It lasts until your next rest or they place the card in their vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Once per {LONG_REST}, this card can mimic the features of another domain card of level 8 or lower in another player's {LOADOUT}. <strong className="inline-strong">Spend {HOPE} equal to half the card's level</strong> to gain access to the feature. It lasts until your next rest or they place the card in their {VAULT}.</p>
      </>
    ),
  },
  {
    name: "Master of the Craft",
    level: 9,
    domain: "Grace",
    type: "Ability",
    recallCost: 0,
    text: "Gain a permanent +2 bonus to two of your Experiences or a permanent +3 bonus to one of your Experiences. Then place this card in your vault permanently.",
    rendered: (
      <>
        <p key={0} className="card-text-para">Gain a permanent +2 bonus to two of your Experiences or a permanent +3 bonus to one of your Experiences. Then place this card in your {VAULT} permanently.</p>
      </>
    ),
  },
  {
    name: "Encore",
    level: 10,
    domain: "Grace",
    type: "Spell",
    recallCost: 1,
    text: "When an ally within Close range deals damage to an adversary, you can make a **Spellcast Roll** against that same target. On a success, you deal the same damage to the target that your ally dealt. If your Spellcast Roll succeeds with Fear, place this card in your vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">When an ally within {CLOSE} range deals damage to an adversary, you can make a {SPELLCAST_ROLL} against that same target. On a success, you deal the same damage to the target that your ally dealt. If your {SPELLCAST_ROLL} succeeds with Fear, place this card in your {VAULT}.</p>
      </>
    ),
  },
  {
    name: "Notorious",
    level: 10,
    domain: "Grace",
    type: "Ability",
    recallCost: 0,
    text: "People know who you are and what you've done, and they treat you differently because of it. When you leverage your notoriety to get what you want, you can **mark a Stress** before you roll to gain a +10 bonus to the result. Your food and drinks are always free wherever you go, and everything else you buy is reduced in price by one bag of gold (to a minimum of one handful).\n\nThis card doesn't count against your loadout's domain card maximum of 5 and can't be placed in your vault.",
    rendered: (
      <>
        <p key={0} className="card-text-para">People know who you are and what you've done, and they treat you differently because of it. When you leverage your notoriety to get what you want, you can <strong className="inline-strong">mark a {STRESS}</strong> before you roll to gain a +10 bonus to the result. Your food and drinks are always free wherever you go, and everything else you buy is reduced in price by one bag of gold (to a minimum of one handful).</p>
        <div key={1} className="card-text-gap" />
        <p key={2} className="card-text-para">This card doesn't count against your {LOADOUT}'s domain card maximum of 5 and can't be placed in your {VAULT}.</p>
      </>
    ),
  },
];

export default graceCards;
